import React, { useEffect, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { api, fmtNGN } from "../lib/api";
import { ErrorState, TableSkeleton } from "../components/LoadState";
import { Download, Calendar } from "lucide-react";

export default function PayrollPage() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/payroll", { params: { month } });
        setData(data);
      } catch (e) {
        setError(e.userMessage || "Failed to load payroll.");
      } finally {
        setLoading(false);
      }
    })();
  }, [month]);

  const exportCSV = () => {
    if (!data) return;
    const header = ["Staff", "Jobs", "Crates", "Earnings"];
    const body = data.rows.map((r) => [`"${r.staff_name}"`, r.jobs, r.crates, r.earnings]);
    const csv = [header.join(","), ...body.map((b) => b.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ledgerflow-payroll-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payroll-page">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Payroll</h1>
            <div className="text-sm text-muted-foreground">Monthly staff earnings aggregate</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-input bg-white">
              <Calendar size={16} className="text-muted-foreground" />
              <input
                data-testid="payroll-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-transparent text-sm focus:outline-none"
              />
            </div>
            <button
              data-testid="payroll-export"
              onClick={exportCSV}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition text-sm font-semibold"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>

        {error ? <div className="mb-5"><ErrorState message={error} /></div> : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Sum label="Entries" value={data?.entries_count ?? "—"} />
          <Sum label="Staff paid" value={data?.rows?.length ?? "—"} />
          <Sum label="Total" value={data ? fmtNGN(data.total) : "—"} accent />
        </div>

        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Staff</th>
                  <th className="px-5 py-3 text-right">Jobs</th>
                  <th className="px-5 py-3 text-right">Crates</th>
                  <th className="px-5 py-3 text-right">Earnings</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeleton rows={5} columns={4} /> : null}
                {!loading && (data?.rows || []).map((r) => (
                  <tr key={r.staff_id} className="border-t border-border/60" data-testid={`payroll-row-${r.staff_id}`}>
                    <td className="px-5 py-3 font-semibold">{r.staff_name}</td>
                    <td className="px-5 py-3 text-right">{r.jobs}</td>
                    <td className="px-5 py-3 text-right">{r.crates}</td>
                    <td className="px-5 py-3 text-right text-money">{fmtNGN(r.earnings)}</td>
                  </tr>
                ))}
                {!loading && data?.rows?.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-10 text-muted-foreground">No payroll for this month yet.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function Sum({ label, value, accent }) {
  return (
    <div className={`card-soft p-5 ${accent ? "bg-emerald-50 border-emerald-100" : ""}`}>
      <div className={`text-3xl font-display font-bold ${accent ? "text-emerald-600" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
